import React, { useState, useContext, useEffect } from 'react';
import { UserContext, CompanyContext } from '../App';
import Navigation from './Navigation';
import Footer from './Footer';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Building2, 
  Upload, 
  FileText, 
  Image, 
  Phone, 
  User, 
  X,
  Edit,
  Save,
  AlertCircle,
  Eye,
  Download
} from 'lucide-react';

const CompanySettings = () => {
  const { user, API } = useContext(UserContext);
  const { company, updateCompany } = useContext(CompanyContext);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    raison_sociale: '',
    numero_ncc: '',
    numero_rccm: '',
    telephone_proprietaire: '',
    nom_proprietaire: '',
    prenom_proprietaire: ''
  });
  const [dfeDocument, setDfeDocument] = useState(null);
  const [rccmDocument, setRccmDocument] = useState(null);
  const [logo, setLogo] = useState(null);

  useEffect(() => {
    if (company) {
      setFormData({
        raison_sociale: company.raison_sociale || '',
        numero_ncc: company.numero_ncc || '',
        numero_rccm: company.numero_rccm || '',
        telephone_proprietaire: company.telephone_proprietaire || '',
        nom_proprietaire: company.nom_proprietaire || '',
        prenom_proprietaire: company.prenom_proprietaire || ''
      });
    }
  }, [company]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (type, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const fileData = {
          data: e.target.result,
          name: file.name,
          size: file.size,
          type: file.type
        };
        
        switch (type) {
          case 'dfe':
            setDfeDocument(fileData);
            toast.success('DFE mise à jour');
            break;
          case 'rccm':
            setRccmDocument(fileData);
            toast.success('RCCM mis à jour');
            break;
          case 'logo':
            if (file.type.startsWith('image/')) {
              setLogo(fileData);
              toast.success('Logo mis à jour');
            } else {
              toast.error('Veuillez sélectionner une image pour le logo');
            }
            break;
        }
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);

      const updatePayload = {
        ...formData,
        owner_user_id: user.id,
        dfe_document: dfeDocument ? dfeDocument.data : company.dfe_document,
        rccm_document: rccmDocument ? rccmDocument.data : company.rccm_document,
        logo: logo ? logo.data : company.logo
      };

      await axios.put(`${API}/company/${company.id}`, updatePayload);

      // Mettre à jour le contexte de l'entreprise
      const updatedCompany = { ...company, ...updatePayload };
      updateCompany(updatedCompany);

      toast.success('Informations de l\'entreprise mises à jour !');
      setEditing(false);
      
      // Reset temporary uploads
      setDfeDocument(null);
      setRccmDocument(null);
      setLogo(null);

    } catch (error) {
      console.error('Error updating company:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = (documentData, filename) => {
    try {
      const link = document.createElement('a');
      link.href = documentData;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Document téléchargé !');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const viewDocument = (documentData, title) => {
    const modal = window.open('', '_blank', 'width=800,height=600');
    modal.document.write(`
      <html>
        <head><title>${title}</title></head>
        <body style="margin:0;background:#f3f4f6;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;font-family:Inter,sans-serif;">
          <div style="background:white;border-radius:8px;padding:20px;box-shadow:0 4px 6px rgba(0,0,0,0.1);margin:20px;">
            <h3 style="margin:0 0 15px 0;color:#374151;">${title}</h3>
            <img src="${documentData}" style="max-width:100%;max-height:70vh;object-fit:contain;border-radius:8px;" alt="${title}" />
            <div style="margin-top:15px;text-align:center;">
              <button onclick="window.close()" style="background:#6366f1;color:white;border:none;padding:8px 16px;border-radius:6px;cursor:pointer;">Fermer</button>
            </div>
          </div>
        </body>
      </html>
    `);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Vérifier les permissions
  if (!user || (user.role !== 'owner' && user.role !== 'system_admin')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Accès Refusé</h2>
            <p className="text-gray-600">Seuls les propriétaires et l'administrateur système peuvent modifier l'entreprise.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Aucune Entreprise</h2>
            <p className="text-gray-600">Aucune entreprise n'est associée à ce compte.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <Navigation />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Paramètres de l'Entreprise
            </h1>
            <p className="text-gray-600">
              Gérez les informations de votre boulangerie
            </p>
          </div>
          
          <Button
            onClick={() => setEditing(!editing)}
            className={`mt-4 sm:mt-0 ${editing ? 'bg-gray-600 hover:bg-gray-700' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
          >
            {editing ? (
              <>
                <X className="h-4 w-4 mr-2" />
                Annuler
              </>
            ) : (
              <>
                <Edit className="h-4 w-4 mr-2" />
                Modifier
              </>
            )}
          </Button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Informations Entreprise */}
            <Card className="bg-white/70 glass border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-indigo-600" />
                  Informations Légales
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Raison Sociale avec Logo */}
                <div className="space-y-4">
                  <div className="flex items-center justify-center mb-4">
                    {(logo?.data || company.logo) ? (
                      <div className="relative group">
                        <img 
                          src={logo?.data || company.logo} 
                          alt="Logo entreprise" 
                          className="w-24 h-24 object-contain rounded-lg border border-gray-200 bg-white p-2"
                        />
                        {editing && (
                          <button
                            type="button"
                            onClick={() => document.getElementById('logo-upload').click()}
                            className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center"
                          >
                            <Upload className="h-6 w-6 text-gray-600 opacity-0 group-hover:opacity-100" />
                          </button>
                        )}
                      </div>
                    ) : editing ? (
                      <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-indigo-400" 
                           onClick={() => document.getElementById('logo-upload').click()}>
                        <Image className="h-8 w-8 text-gray-400" />
                      </div>
                    ) : (
                      <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Building2 className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {editing && (
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload('logo', e)}
                      className="hidden"
                      id="logo-upload"
                    />
                  )}

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Raison Sociale
                    </Label>
                    {editing ? (
                      <Input
                        value={formData.raison_sociale}
                        onChange={(e) => handleInputChange('raison_sociale', e.target.value)}
                        className="h-12 text-base"
                        required
                      />
                    ) : (
                      <p className="h-12 flex items-center px-4 bg-gray-50 rounded-lg text-gray-900 font-medium">
                        {company.raison_sociale}
                      </p>
                    )}
                  </div>
                </div>

                {/* NCC */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Numéro NCC
                  </Label>
                  {editing ? (
                    <Input
                      value={formData.numero_ncc}
                      onChange={(e) => handleInputChange('numero_ncc', e.target.value)}
                      className="h-12 text-base"
                      required
                    />
                  ) : (
                    <p className="h-12 flex items-center px-4 bg-gray-50 rounded-lg text-gray-900 font-mono">
                      {company.numero_ncc}
                    </p>
                  )}
                </div>

                {/* DFE */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    DFE (Déclaration Fiscale d'Existence)
                  </Label>
                  
                  {editing && (
                    <>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileUpload('dfe', e)}
                        className="hidden"
                        id="dfe-upload-edit"
                      />
                      <Button
                        type="button"
                        onClick={() => document.getElementById('dfe-upload-edit').click()}
                        variant="outline"
                        className="w-full h-12 border-2 border-dashed"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {dfeDocument ? 'Remplacer la DFE' : 'Télécharger la DFE'}
                      </Button>
                    </>
                  )}
                  
                  {(dfeDocument || company.dfe_document) && (
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">
                          {dfeDocument ? dfeDocument.name : 'DFE actuelle'}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => viewDocument(dfeDocument?.data || company.dfe_document, 'DFE - Déclaration Fiscale d\'Existence')}
                          className="text-blue-600 hover:text-blue-800"
                          title="Voir le document"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadDocument(dfeDocument?.data || company.dfe_document, `DFE_${company.raison_sociale}.pdf`)}
                          className="text-green-600 hover:text-green-800"
                          title="Télécharger"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* RCCM */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Numéro RCCM
                  </Label>
                  {editing ? (
                    <Input
                      value={formData.numero_rccm}
                      onChange={(e) => handleInputChange('numero_rccm', e.target.value)}
                      className="h-12 text-base"
                      required
                    />
                  ) : (
                    <p className="h-12 flex items-center px-4 bg-gray-50 rounded-lg text-gray-900 font-mono">
                      {company.numero_rccm}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Document RCCM
                  </Label>
                  
                  {editing && (
                    <>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileUpload('rccm', e)}
                        className="hidden"
                        id="rccm-upload-edit"
                      />
                      <Button
                        type="button"
                        onClick={() => document.getElementById('rccm-upload-edit').click()}
                        variant="outline"
                        className="w-full h-12 border-2 border-dashed"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {rccmDocument ? 'Remplacer le RCCM' : 'Télécharger le RCCM'}
                      </Button>
                    </>
                  )}
                  
                  {(rccmDocument || company.rccm_document) && (
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">
                          {rccmDocument ? rccmDocument.name : 'RCCM actuel'}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => viewDocument(rccmDocument?.data || company.rccm_document, 'RCCM - Registre du Commerce')}
                          className="text-blue-600 hover:text-blue-800"
                          title="Voir le document"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadDocument(rccmDocument?.data || company.rccm_document, `RCCM_${company.raison_sociale}.pdf`)}
                          className="text-green-600 hover:text-green-800"
                          title="Télécharger"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Informations Propriétaire */}
            <Card className="bg-white/70 glass border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-green-600" />
                  Informations Propriétaire
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Nom
                  </Label>
                  {editing ? (
                    <Input
                      value={formData.nom_proprietaire}
                      onChange={(e) => handleInputChange('nom_proprietaire', e.target.value)}
                      className="h-12 text-base"
                      required
                    />
                  ) : (
                    <p className="h-12 flex items-center px-4 bg-gray-50 rounded-lg text-gray-900 font-medium">
                      {company.nom_proprietaire}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Prénom
                  </Label>
                  {editing ? (
                    <Input
                      value={formData.prenom_proprietaire}
                      onChange={(e) => handleInputChange('prenom_proprietaire', e.target.value)}
                      className="h-12 text-base"
                      required
                    />
                  ) : (
                    <p className="h-12 flex items-center px-4 bg-gray-50 rounded-lg text-gray-900 font-medium">
                      {company.prenom_proprietaire}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Numéro de Téléphone
                  </Label>
                  {editing ? (
                    <Input
                      value={formData.telephone_proprietaire}
                      onChange={(e) => handleInputChange('telephone_proprietaire', e.target.value)}
                      className="h-12 text-base"
                      required
                    />
                  ) : (
                    <p className="h-12 flex items-center px-4 bg-gray-50 rounded-lg text-gray-900 font-mono">
                      {company.telephone_proprietaire}
                    </p>
                  )}
                </div>

                {/* Dates d'information */}
                <div className="border-t border-gray-200 pt-4 space-y-2">
                  <div className="text-sm text-gray-600">
                    <p><strong>Entreprise créée le:</strong> {new Date(company.created_at).toLocaleDateString('fr-FR')}</p>
                    {company.updated_at && (
                      <p><strong>Dernière mise à jour:</strong> {new Date(company.updated_at).toLocaleDateString('fr-FR')}</p>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                {editing && (
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium rounded-lg shadow-lg"
                  >
                    {loading ? (
                      <>
                        <div className="spinner mr-2"></div>
                        Sauvegarde...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Sauvegarder les Modifications
                      </>
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </form>
      </div>
      
      <Footer />
    </div>
  );
};

export default CompanySettings;